import { FC } from 'react';
import clsx from 'clsx';
import { Accordion, AccordionProps } from './Accordion';
import { Typography, TypographyVariants } from '../Typography';
import {
  AddPlayer,
  ArrowLeft,
  Assist,
  Captain,
  CloseRounded,
  Goal,
  Injured,
  PenaltyMiss,
  PenaltySave,
  RedTicket,
  Reset,
  Ticket,
  Transfer,
  TransferInOut,
  Unavailable,
  ViceCaptain,
  YellowTicket,
} from '../svgs';

export type GameKey =
  | 'Goal'
  | 'Assist'
  | 'Own Goal'
  | 'Yellow'
  | 'Red'
  | 'Sub On'
  | 'Sub Off'
  | 'Penalty Miss'
  | 'Penalty Save'
  | 'Add Player'
  | 'Remove'
  | 'Injured'
  | 'Suspended'
  | 'Captain'
  | 'Vice Captain'
  | 'Unavailable'
  | 'Transfers'
  | 'Transfer Out'
  | 'Transfer In'
  | 'Reset';

export interface GameKeysAccordionProps extends Omit<
  AccordionProps,
  'accordions' | 'type' | 'defaultValue'
> {
  className?: string;

  title: string;
  titleVariant?: TypographyVariants;
  titleClassName?: string;

  gameKeys: GameKey[];
  onToggle?: (expanded: boolean) => void;
}

export const GameKeysAccordion: FC<GameKeysAccordionProps> = ({
  className,
  title,
  collapsible = true,
  titleVariant = 'label030',
  titleClassName,
  gameKeys,
  onToggle,
  ...otherProps
}) => {
  const _gameKeys = gameKeys.map(gameKey => {
    switch (gameKey) {
      // ? i18n
      case 'Assist':
        return {
          Icon: Assist,
          className: 'text-[--color-neurtal-1000]',
          label: 'Assist',
        };
      case 'Goal':
        return {
          Icon: Goal,
          className: 'text-[--color-neurtal-1000]',
          label: 'Goal',
        };
      case 'Own Goal':
        return {
          Icon: Goal,
          className: 'text-[--color-red-200]',
          label: 'Own Goal',
        };
      case 'Penalty Miss':
        return {
          Icon: PenaltyMiss,
          className: 'text-[--color-red-200]',
          label: 'Penalty Miss',
        };
      case 'Penalty Save':
        return {
          Icon: PenaltySave,
          className: 'text-[--color-blue-200]',
          label: 'Penalty Save',
        };
      case 'Red':
        return {
          Icon: RedTicket,
          className: '',
          label: 'Red',
        };
      case 'Yellow':
        return {
          Icon: YellowTicket,
          className: '',
          label: 'Yellow',
        };
      case 'Sub Off':
        return {
          Icon: TransferInOut,
          className: 'text-[--color-red-200] rotate-180',
          label: 'Sub Off',
        };
      case 'Sub On':
        return {
          Icon: TransferInOut,
          className: 'text-[--color-green-200] rotate-0',
          label: 'Sub On',
        };
      case 'Add Player':
        return {
          Icon: AddPlayer,
          className: 'text-[--button-contained-blue-base-bg]',
          label: 'Add Player',
        };
      case 'Remove':
        return {
          Icon: CloseRounded,
          className: 'text-[--color-neurtal-1000]',
          label: 'Remove',
        };
      case 'Injured':
        return {
          Icon: Injured,
          className: 'text-[--color-red-200]',
          label: 'Injured',
        };
      case 'Suspended':
        return {
          Icon: Ticket,
          className: '',
          label: 'Suspended',
        };
      case 'Captain':
        return {
          Icon: Captain,
          className: 'text-[#A036E0]',
          label: 'Captain',
        };
      case 'Vice Captain':
        return {
          Icon: ViceCaptain,
          className: 'text-[#A036E0]',
          label: 'Vice Captain',
        };
      case 'Unavailable':
        return {
          Icon: Unavailable,
          className: 'text-[--color-red-200]',
          label: 'Unavailable',
        };
      case 'Transfers':
        return {
          Icon: Transfer,
          className: 'text-[--button-contained-blue-base-bg]',
          label: 'Transfers',
        };
      case 'Transfer Out':
        return {
          Icon: ArrowLeft,
          className: 'text-[--color-red-200] rotate-180',
          label: 'Transfer Out',
        };
      case 'Transfer In':
        return {
          Icon: ArrowLeft,
          className: 'text-[--color-green-200]',
          label: 'Transfer In',
        };
      case 'Reset':
        return {
          Icon: Reset,
          className: 'text-[--color-neurtal-1000]',
          label: 'Reset',
        };
    }
  });
  return (
    <Accordion
      type={'single'}
      defaultValue={undefined}
      collapsible={collapsible}
      className={className}
      {...otherProps}
      accordions={[
        {
          value: '0',
          onToggle,
          trigger: (
            <Typography variant={titleVariant} className={titleClassName}>
              {title}
            </Typography>
          ),
          content: (
            <div className="grid grid-cols-3 gap-y-[--padding-md]">
              {_gameKeys.map(({ Icon, label, className }, index) => (
                <div
                  key={index}
                  className="flex flex-1 flex-row gap-x-[--padding-xs]  items-center"
                >
                  <Icon className={clsx('h-[20px] w-[20px]', className)} />
                  <Typography
                    variant="paragraph010"
                    className="!text-[10px] lg:!text-[14px] text-[#222526]"
                  >
                    {label}
                  </Typography>
                </div>
              ))}
            </div>
          ),
        },
      ]}
    />
  );
};
